import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  getRedirectResult,
  onIdTokenChanged,
  signInWithCredential,
  signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
  signInWithPopup,
  signOut as signOutFromFirebase,
  type User as FirebaseUser,
} from 'firebase/auth'

import { FirebaseService } from 'services/FirebaseService'
import { webLogger } from 'utils/logger'

export type AppAuthUser = {
  displayName: string | null
  email: string | null
  id: string
}

type AuthChangeListener = (user: AppAuthUser | undefined) => void

type AuthClientAdapter = {
  getAccessToken: (forceRefresh?: boolean) => Promise<string | undefined>
  getCurrentUser: () => Promise<AppAuthUser | undefined>
  signInWithEmailAndPassword: (email: string, password: string) => Promise<void>
  signUpWithEmailAndPassword: (email: string, password: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signInWithGoogleIdToken: (token: string, nonce?: string) => Promise<void>
  signOut: () => Promise<void>
  subscribe: (listener: AuthChangeListener) => () => void
}

const mapFirebaseUser = (user: FirebaseUser | null): AppAuthUser | undefined => {
  if (!user) {
    return undefined
  }

  return {
    displayName: user.displayName,
    email: user.email,
    id: user.uid,
  }
}

const createFirebaseAuthAdapter = (): AuthClientAdapter => {
  const auth = FirebaseService.getAuth()
  let redirectUser: FirebaseUser | null = null
  const redirectResultReady = getRedirectResult(auth)
    .then(result => {
      redirectUser = result?.user ?? null
      return undefined
    })
    .catch((error: unknown) => {
      webLogger.warn('Failed to resolve Firebase redirect result', { error })
      return undefined
    })

  return {
    getAccessToken: async (forceRefresh = false) => {
      return auth.currentUser ? auth.currentUser.getIdToken(forceRefresh) : undefined
    },
    getCurrentUser: async () => {
      await redirectResultReady
      await auth.authStateReady()
      return mapFirebaseUser(auth.currentUser ?? redirectUser)
    },
    signInWithEmailAndPassword: async (email: string, password: string) => {
      await firebaseSignInWithEmailAndPassword(auth, email, password)
    },
    signUpWithEmailAndPassword: async (email: string, password: string) => {
      await createUserWithEmailAndPassword(auth, email, password)
    },
    signInWithGoogle: async () => {
      await signInWithPopup(auth, new GoogleAuthProvider())
    },
    signInWithGoogleIdToken: async (token: string, nonce?: string) => {
      const credential = GoogleAuthProvider.credential(token, nonce)
      await signInWithCredential(auth, credential)
    },
    signOut: async () => {
      await signOutFromFirebase(auth)
    },
    subscribe: listener => {
      return onIdTokenChanged(auth, user => {
        listener(mapFirebaseUser(user))
      })
    },
  }
}

const appAuthMode = 'firebase'

const activeAuthClient: AuthClientAdapter = (() => {
  try {
    return createFirebaseAuthAdapter()
  } catch (error) {
    webLogger.error('Failed to initialize auth client', { authMode: appAuthMode, error })
    throw error
  }
})()

export const AuthService = {
  authMode: appAuthMode,
  getAccessToken: (forceRefresh = false) => activeAuthClient.getAccessToken(forceRefresh),
  getCurrentUser: () => activeAuthClient.getCurrentUser(),
  signInWithEmailAndPassword: (email: string, password: string) =>
    activeAuthClient.signInWithEmailAndPassword(email, password),
  signUpWithEmailAndPassword: (email: string, password: string) =>
    activeAuthClient.signUpWithEmailAndPassword(email, password),
  signInWithGoogle: () => activeAuthClient.signInWithGoogle(),
  signInWithGoogleIdToken: (token: string, nonce?: string) => activeAuthClient.signInWithGoogleIdToken(token, nonce),
  signOut: () => activeAuthClient.signOut(),
  subscribe: (listener: AuthChangeListener) => activeAuthClient.subscribe(listener),
}
